import React, { useMemo, useState } from "react";
import { LoadingSkeletonSingleCard, LoadingSkeletonMultiList, EmptyState } from "./content-placeholder";
import {
  ContentGroup,
  VideoListItem,
  VideoListSingleItem,
  PodcastListItem,
  MomentListItem,
  PostListItem,
  ArticleListItem,
  FailedMessageListItem,
  GeneratingMessageListItem,
  AiCreationVideoItem,
  AiCreationImageItem,
  GeneratingAiCreationMessageListItem,
} from "./message";
import {
  GenericWidgetListView,
} from "./generic-widget-list-view";
import { ThinkingData } from "@/services/push-info-origin-data-record-list";
import { TASK_STATUS } from "@/manager/manager/agent-user-messages-manager";
import { useTranslation } from "react-i18next";
import { AiMakeMessageRecordData } from "@/services/ai-make-source-record-list-by-type";
import { getModelTypeByValue } from "./constants/ai-creation-model";


// 模板项
export type TemplateItem = {
  id: string;
  coverUrl: string;
  name: string;
};

export type VoiceItem = {
  id: string;
  sampleUrl: string;
  name: string;
};

interface Message {
  id: number;
  agentId?: number;
  type: number;
  title?: string;
  content?: string;
  inputText?: string;
  coverImageUrl?: string;
  videoUrl?: string;
  imageUrls?: string;
  audioUrl?: string;
  createdAt?: number;
  taskId?: string;
  status?: number;
  taskStatus?: (typeof TASK_STATUS)[keyof typeof TASK_STATUS];
  videoDuration?: number;
  audioDuration?: number;
  groupKey: string;
  summary?: string;
  noteName?: string;
  s3AudioUrl?: string;
  s3CoverImageUrl?: string;
  s3ImageUrls?: string;
  s3VideoUrl?: string;
  noteContentTitle?: string;
  linkUrl?: string;
  n8nCode?: string;
  n8nMessage?: string;
  errMessage?: string;
  thinkingList?: ThinkingData[];
  autoPush?: boolean;
  reqBody?: string;
  inputAddress?: string;
  aiMakeSourceRecords?: AiMakeMessageRecordData[];
}

interface ContentProps {
  isLoading: boolean;
  isLoadingMore?: boolean;
  scrollToBottomToken?: number;
  isException?: boolean;
  isExpired?: boolean;
  expiredAt?: string;
  messages: Message[];
  deleteLoading?: boolean;
  deleteCreationOneofRecordLoading?: boolean;
  onOpenVideo?: (payload: { videoUrl: string; poster?: string; text: string; id: number; taskId: string; autoPush?: boolean }) => void;
  onOpenAiCreationVideo?: (payload: { videoUrl: string; poster?: string; text: string; id: number; taskId: string; autoPush?: boolean, aspectRatio?: string; model?: string; imageSize?: string; duration?: number, inputAddress?: string; }) => void;
  onOpenAiCreationImage?: (payload: { currentIndex: number; imageUrls: string[]; text: string; id: number; taskId: string; autoPush?: boolean; aspectRatio?: string; model?: string; imageSize?: string; inputAddress?: string; }) => void;
  onOpenMoment?: (payload: { images: string[]; text: string; id: number; taskId: string; autoPush?: boolean }) => void;
  onOpenPost?: (payload: { images: string[]; title: string; text: string; id: number; taskId: string; autoPush?: boolean }) => void;
  onOpenArticle?: (payload: { title: string; contentHtml: string; image?: string; publishedAt?: number; summary?: string; id: number; taskId: string; autoPush?: boolean }) => void;
  onRefresh?: () => Promise<void>;
  onLoad?: () => Promise<void>;
  onScrollTemplatesChange?: (show: boolean) => void;
  onDeleteMessage?: (id: number | number[]) => void;
  onDeleteCreationOneofRecord?: (params: { ids: number | number[]; taskId: string }) => void;
  onGenerateMessage?: (taskId: string) => void;
  onEditMessage?: (taskId: string) => void;
  onUseReferenceImage?: (url: string) => void;
  onCancel?: (params: { taskId: string; type: number }) => void;
};

export const Content: React.FC<ContentProps> = ({
  isLoading,
  messages = [],
  isException,
  isExpired,
  expiredAt,
  scrollToBottomToken,
  deleteLoading,
  deleteCreationOneofRecordLoading,
  onOpenVideo,
  onOpenAiCreationVideo,
  onOpenAiCreationImage,
  onOpenMoment,
  onOpenPost,
  onOpenArticle,
  onRefresh,
  onLoad,
  onScrollTemplatesChange,
  onDeleteMessage,
  onDeleteCreationOneofRecord,
  onGenerateMessage,
  onEditMessage,
  onUseReferenceImage,
  onCancel,
}) => {
  const { t } = useTranslation();

  // 当前激活的播客ID
  const [activePodcastId, setActivePodcastId] = useState<number | null>(null);

  const typeMap = {
    1: t('agent_creation.video'),
    2: t('agent_creation.podcast'),
    3: t('agent_creation.moments'),
    4: t('agent_creation.post'),
    5: t('agent_creation.article'),
  }

  const children = useMemo(() => {
    if (isLoading) {
      return [
        <LoadingSkeletonSingleCard key="sk-single-01" />,
        <LoadingSkeletonMultiList key="sk-multi" />,
        <LoadingSkeletonSingleCard key="sk-single-02" />,
      ];
    }

    if (!messages || messages.length === 0) {
      return [<EmptyState key="empty" />];
    }

    const grouped = messages?.reduce<Record<string, Message[]>>((acc, m) => {
      const key = `${m.groupKey} ${m.type}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(m);
      return acc;
    }, {});

    return Object.entries(grouped).map(([key, list]) => {
      const parts = key.split(" ");
      const date = parts[0] ?? "";
      const time = parts[1] ?? "";

      const first = list[0];
      const thinkingList = first?.thinkingList || [];

      // 等待中
      const isQueued = first.status === 0 && (
        first.taskStatus === TASK_STATUS.QUEUED
        || first.taskStatus === TASK_STATUS.PENDING
        || !first.taskStatus
      );
      // 思考中
      const isThinking = first.status === 0 && first.taskStatus === TASK_STATUS.THINKING;
      // 生成中
      const isProcessing = first.status === 0 && first.taskStatus === TASK_STATUS.PROCESSING;
      // 成功
      const isSuccess = first.status === 1
      // 失败
      const isFailed = first.status !== 0 && first.status !== 1 && first.status !== 3 && first.status !== 10
      // 已取消
      const isCancelled = first.status == 3
      // 已超时
      const isTimeout = first.status == 10

      const reqBody = first.reqBody ? JSON.parse(first.reqBody) : {}

      // AI创作类型
      const aiCreationType = getModelTypeByValue(reqBody.model || reqBody.modelId);

      // 失败或超时
      if (isFailed || isTimeout) return (
        <FailedMessageListItem
          key={first.id}
          failedType={'failed'}
          thumbnail={first.coverImageUrl}
          type={first.type}
          isAiCreationMessage={first.type === 6}
          duration={reqBody.duration}
          aspectRatio={reqBody.aspectRatio}
          imageSize={reqBody.imageSize || reqBody?.resolution}
          aiCreationModel={reqBody?.modelId || reqBody?.model}
          isFontEndFrameImage={reqBody?.isFontEndFrameImage}
          inputText={first.inputText}
          noteName={first.noteContentTitle}
          linkUrl={first.linkUrl}
          inputAddress={first.inputAddress}
          thinkingList={thinkingList}
          thinkingTitle={
            isFailed ? `${t('agent_creation.content.generate_failed', { type: typeMap[first.type] })}${(first.errMessage || first.n8nMessage) ? `: ${first.errMessage || first.n8nMessage}` : ''}`
              : first.errMessage || t('agent_creation.content.generate_timeout', { type: typeMap[first.type] })
          }
          createdAt={`${date} ${time}`}
          onDelete={async () => onDeleteMessage?.(first.id)}
          deleteLoading={deleteLoading}
          onRegenerate={() => onGenerateMessage?.(first.taskId)}
          onEdit={() => onEditMessage?.(first.taskId)}
        />
      );

      // 已取消
      if (isCancelled) return (
        <FailedMessageListItem
          key={first.id}
          failedType="cancel"
          thumbnail={first.coverImageUrl}
          type={first.type}
          inputText={first.inputText}
          noteName={first.noteContentTitle}
          linkUrl={first.linkUrl}
          thinkingList={thinkingList}
          thinkingTitle={t('agent_creation.content.thinking_process.interrupted')}
          createdAt={`${date} ${time}`}
          onDelete={async () => onDeleteMessage?.(first.id)}
          deleteLoading={deleteLoading}
          onRegenerate={() => onGenerateMessage?.(first.taskId)}
          onEdit={() => onEditMessage?.(first.taskId)}
        />
      )

      // Ai创作的生成中
      if (list.length === 1 && first.status == 0 && first.type === 6) {
        return (
          <GeneratingAiCreationMessageListItem
            key={first.id}
            type={first.type}
            inputText={first.inputText}
            createdAt={first.createdAt}
            inputAddress={[first.inputAddress, reqBody.fileUrl1].filter(Boolean).join(',')}
            model={reqBody?.modelId || reqBody?.model}
            duration={reqBody?.duration}
            aspectRatio={reqBody?.aspectRatio}
            imageSize={reqBody?.imageSize || reqBody?.resolution}
            isFontEndFrameImage={!!reqBody.fileUrl1}
          />
        )
      }

      // 生成中
      if (list.length === 1 && first.status == 0) return (
        <GeneratingMessageListItem
          key={first.id}
          noteName={first.noteContentTitle}
          type={first.type}
          thumbnail={first.coverImageUrl}
          inputText={first.inputText}
          linkUrl={first.linkUrl}
          createdAt={`${date} ${time}`}
          thinkingCreateTime={first.thinkingList?.[first.thinkingList?.length - 1]?.startAt}
          thinkingList={thinkingList}
          thinkingTitle={t("agent_creation.content.thinking_process.in_deep_thinking")}
          isThinking={isThinking}
          isQueued={isQueued}
          isProcessing={isProcessing}
          showCancel={!!first.taskStatus} // 第一次轮询回来后展示取消按钮
          onCancel={() => onCancel({ taskId: first.taskId, type: first.type })}
        />
      );

      // 成功消息
      return (
        <ContentGroup
          key={key}
          type={first.type}
          isAiCreationMessage={first.type === 6}
          aiCreationType={aiCreationType}
          date={new Date(`${date} ${time}`).getTime()}
          inputText={first.inputText}
          thumbnail={first.s3CoverImageUrl || first.coverImageUrl}
          noteName={first.noteContentTitle}
          linkUrl={first.linkUrl}
          itemCount={list.length}
          onDelete={async () => onDeleteMessage?.(list.map((i) => i.id))}
          deleteLoading={deleteLoading}
          showThinkingProcess={true}
          thinkingTitle={t('agent_creation.content.thinking_process.thinking_completed')}
          thinkingList={thinkingList}
          aiCreationModel={reqBody?.modelId || reqBody?.model}
          duration={reqBody?.duration}
          aspectRatio={reqBody?.aspectRatio}
          imageSize={reqBody?.imageSize || reqBody?.resolution}
          inputAddress={[first.inputAddress, reqBody.fileUrl1].filter(Boolean).join(',')}
          isFontEndFrameImage={!!reqBody.fileUrl1}
          onRegenerate={() => onGenerateMessage?.(first.taskId)}
          onReedit={() => onEditMessage?.(first.taskId)}
          children={list.map((item) => {
            switch (item.type) {
              case 1: {
                const VideoElement = list.length === 1 ? VideoListSingleItem : VideoListItem;
                return (
                  <VideoElement
                    key={item.id}
                    videoThumbnail={item.s3CoverImageUrl || item.coverImageUrl}
                    videoUrl={item.s3VideoUrl || item.videoUrl}
                    content={item.content}
                    onDelete={async () => onDeleteMessage?.(item.id)}
                    deleteLoading={deleteLoading}
                    duration={item.videoDuration}
                    onTap={async () => {
                      onOpenVideo?.({
                        videoUrl: item.s3VideoUrl || item.videoUrl,
                        poster: item.s3CoverImageUrl || item.coverImageUrl,
                        text: item.content,
                        id: item.id,
                        taskId: item.taskId,
                        autoPush: item.autoPush,
                      });
                    }}
                  />
                );
              }
              case 2:
                return (
                  <PodcastListItem
                    key={item.id}
                    voiceThumbnail={item.s3CoverImageUrl || item.coverImageUrl}
                    voiceUrl={item.s3AudioUrl || item.audioUrl}
                    title={item.title}
                    showDelete={list.length > 1}
                    deleteLoading={deleteLoading}
                    duration={item.audioDuration}
                    isActive={activePodcastId === item.id}
                    onDelete={() => onDeleteMessage?.(item.id)}
                    onTogglePlay={(showControls) => {
                      setActivePodcastId(showControls ? item.id : null);
                    }}
                  />
                );
              case 3:
                return (
                  <MomentListItem
                    key={item.id}
                    images={item.imageUrls.split(",")}
                    content={item.content}
                    showDelete={list.length > 1}
                    onDelete={() => onDeleteMessage?.(item.id)}
                    deleteLoading={deleteLoading}
                    onTap={async () => {
                      onOpenMoment?.({
                        images: item.imageUrls.split(","),
                        text: item.content,
                        id: item.id,
                        taskId: item.taskId,
                        autoPush: item.autoPush,
                      });
                    }}
                  />
                );
              case 4:
                return (
                  <PostListItem
                    key={item.id}
                    images={item.imageUrls.split(",")}
                    title={item.title}
                    showDelete={list.length > 1}
                    onDelete={() => onDeleteMessage?.(item.id)}
                    deleteLoading={deleteLoading}
                    onTap={async () => {
                      onOpenPost?.({
                        images: item.imageUrls.split(","),
                        title: item.title,
                        text: item.content,
                        id: item.id,
                        taskId: item.taskId,
                        autoPush: item.autoPush,
                      });
                    }}
                  />
                );
              case 5:
                return (
                  <ArticleListItem
                    key={item.id}
                    image={item.s3CoverImageUrl || item.coverImageUrl}
                    title={item.title}
                    showDelete={list.length > 1}
                    onDelete={() => onDeleteMessage?.(item.id)}
                    deleteLoading={deleteLoading}
                    onTap={async () => {
                      onOpenArticle?.({
                        title: item.title,
                        contentHtml: item.content,
                        image: item.s3CoverImageUrl || item.coverImageUrl,
                        publishedAt: item.createdAt,
                        summary: item.summary,
                        id: item.id,
                        taskId: item.taskId,
                        autoPush: item.autoPush,
                      });
                    }}
                  />
                );
              case 6:
                {
                  const reqBody = JSON.parse(item.reqBody)

                  if (getModelTypeByValue(reqBody?.model || reqBody.modelId) === 'video') {
                    return (
                      <AiCreationVideoItem
                        key={item.id}
                        records={item.aiMakeSourceRecords}
                        videoThumbnail={item.s3CoverImageUrl || item.coverImageUrl}
                        deleteLoading={deleteLoading}
                        onDelete={async () => onDeleteMessage?.(item.id)}
                        onTap={async () => {
                          onOpenAiCreationVideo?.({
                            videoUrl: item.aiMakeSourceRecords?.[0]?.s3Url || item.aiMakeSourceRecords?.[0]?.url,
                            poster: item.s3CoverImageUrl || item.coverImageUrl,
                            text: item.inputText,
                            id: item.id,
                            taskId: item.taskId,
                            autoPush: item.autoPush,
                            imageSize: reqBody.imageSize || reqBody?.resolution,
                            aspectRatio: reqBody.aspectRatio,
                            model: reqBody.modelId || reqBody.model,
                            duration: item.videoDuration || reqBody.duration,
                            inputAddress: [item.inputAddress, reqBody.fileUrl1].filter(Boolean).join(','),
                          });
                        }}
                      />
                    )
                  }
                  return (
                    <AiCreationImageItem
                      key={item.id}
                      imageUrls={[...item.imageUrls.split(",")]}
                      records={item.aiMakeSourceRecords}
                      aspectRatio={reqBody.aspectRatio}
                      deleteLoading={deleteCreationOneofRecordLoading}
                      onUseReferenceImage={(url) => onUseReferenceImage?.(url)}
                      onDelete={async (id) => onDeleteCreationOneofRecord?.({
                        ids: id,
                        taskId: item.taskId,
                      })}
                      onTap={async (index) => {
                        const urlItem = item.aiMakeSourceRecords?.[index];
                        const imageUrls = item.aiMakeSourceRecords?.filter((i) => i.s3Url || i.url).map((i) => i.s3Url || i.url)
                        // 找到当前点击的图片在新数组中的索引
                        const cIndex = imageUrls.indexOf(urlItem?.s3Url || urlItem?.url);
                        onOpenAiCreationImage?.({
                          imageUrls: imageUrls,
                          inputAddress: [item.inputAddress, reqBody.fileUrl1].filter(Boolean).join(','),
                          text: item.inputText,
                          id: item.id,
                          taskId: item.taskId,
                          autoPush: item.autoPush,
                          aspectRatio: reqBody.aspectRatio,
                          model: reqBody.modelId || reqBody.model,
                          imageSize: reqBody.imageSize || reqBody?.resolution,
                          currentIndex: cIndex >= 0 ? cIndex : 0,
                        });
                      }}
                    />
                  );
                }
              default:
                return null;
            }
          })}
        />
      );
    });
  }, [isLoading, messages, onOpenVideo, onOpenMoment, onOpenPost, onOpenArticle, activePodcastId]);

  return <GenericWidgetListView
    isLoading={isLoading}
    scrollToBottomToken={scrollToBottomToken}
    showException={isException}
    showExpired={isExpired}
    expiredAt={expiredAt}
    children={children}
    onRefresh={onRefresh}
    onLoadMore={onLoad}
    onScrollStateChange={({ nearTop }) => {
      onScrollTemplatesChange?.(nearTop);
    }}
  />;
};
